/**
 * API Client Configuration
 *
 * Uses VITE_API_URL environment variable for production deployments.
 * Falls back to relative URLs for development (proxy handles routing).
 */

// API base URL
// - Uses VITE_API_URL if set (staging/production)
// - Falls back to empty string for Vite dev server proxy
const API_BASE_URL = import.meta.env.VITE_API_URL ?? '';

/**
 * Build a full API URL from a path
 */
export function apiUrl(path: string): string {
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE_URL}${normalizedPath}`;
}

/**
 * Returns mock auth headers for local dev and staging.
 * Empty object in production (where Cloudflare Access handles auth).
 *
 * Enabled when:
 * - VITE_MOCK_AUTH=true is set at build time (staging builds), OR
 * - Running in Vite dev server (import.meta.env.DEV)
 */
export function mockAuthHeaders(): Record<string, string> {
  if (import.meta.env.DEV || import.meta.env.VITE_MOCK_AUTH === 'true') {
    return {
      'X-Mock-User-Role': 'procurement',
      'X-Mock-User-Email': 'procurement@abetech.com',
    };
  }
  return {};
}

/**
 * Fetch wrapper with common configuration
 */
export async function apiFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const url = apiUrl(path);

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
      ...options.headers,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    // Read response text first, then try to parse as JSON
    const responseText = await response.text();
    let errorMessage = `API error: ${response.status}`;

    try {
      const error = JSON.parse(responseText);
      errorMessage = error.error?.message ?? errorMessage;
    } catch {
      // JSON parse failed - use response text if available, otherwise statusText
      errorMessage = responseText || response.statusText || errorMessage;
    }

    throw new Error(errorMessage);
  }

  return response.json();
}
